import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Content = styled.div``;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #00d4ff;
`;

const Text = styled(motion.p)`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #b8b8b8;
  margin-bottom: 1.5rem;
`;

const Highlight = styled.span`
  color: #00d4ff;
  font-weight: 600;
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;
  background: rgba(0, 212, 255, 0.1);
  border-radius: 15px;
  border: 1px solid rgba(0, 212, 255, 0.2);
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #00d4ff;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #b8b8b8;
`;

const ImageContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const ProfileImage = styled.div`
  width: 300px;
  height: 300px;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  border: 4px solid #00d4ff;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    animation: shine 3s infinite;
    z-index: 1;
  }

  @keyframes shine {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
  }
`;

const About = () => {
  return (
    <AboutSection id="about">
      <Container>
        <Content>
          <Title
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            About Me
          </Title>
          
          <Text
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            I'm a passionate <Highlight>Blockchain Developer</Highlight> with 3+ years of experience 
            in <Highlight>Solidity, Ethereum, and Polygon</Highlight>. I've built DeFi systems, NFT platforms, 
            compliance-based tokens, and Web3 integrations.
          </Text>
          
          <Text
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            With a strong focus on <Highlight>security, testing, and gas optimization</Highlight>, 
            I also bring hands-on <Highlight>DevOps experience</Highlight> deploying and maintaining 
            production blockchain applications. My recent work includes developing Layer-1 blockchain nodes 
            and implementing P2P networking solutions.
          </Text>

          <StatsContainer
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <StatItem
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>3+</StatNumber>
              <StatLabel>Years Experience</StatLabel>
            </StatItem>
            <StatItem
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>4+</StatNumber>
              <StatLabel>Projects Completed</StatLabel>
            </StatItem>
            <StatItem
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>5+</StatNumber>
              <StatLabel>Token Standards</StatLabel>
            </StatItem>
            <StatItem
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <StatNumber>3+</StatNumber>
              <StatLabel>Blockchain Platforms</StatLabel>
            </StatItem>
          </StatsContainer>
        </Content>

        <ImageContainer
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <ProfileImage>
            <img 
              src="/images/WhatsApp Image 2025-12-15 at 9.00.39 PM.jpeg" 
              alt="Satish Mukhekar - Blockchain Developer"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </ProfileImage>
        </ImageContainer>
      </Container>
    </AboutSection>
  );
};

export default About;